/**
 * @fileoverview How a line of the week (the brief's todo.week.lines) is drawn,
 * wherever it is: its icon and its ink. The Plants page's weather bar, the
 * filter sheet's "This week" chips and the Weather page's garden week all
 * draw the same server line the same way.
 *
 * A danger (frost, too cold, too hot) wears the warning triangle, as the bar
 * always drew its dangers; the rest wear their condition's icon (a
 * thermometer with a snowflake for a chilly night, rain for a downpour) and
 * the water line the watering can the cards use. The ink is the line's tone
 * (the backend's choice of verdict colour family), plain for wind and the
 * calendar.
 *
 * Where: GardenConditionsBar, PlantFilterSheet, WeatherPage.
 * API: none.
 *
 * @module weekLineLook
 */

import {
  AlertTriangle, CalendarDays, CloudRain, ThermometerSnowflake, ThermometerSun, Wind, type LucideIcon,
} from 'lucide-react';
import { WateringCan } from '@/shared/components/icons/WateringCan';
import { TOKEN_TEXT } from '@/shared/theme/verdictTokens';
import type { BriefWeekLine } from '@/shared/api/todayBrief';

/** A rule line's icon by what its rule reads (RuleLine.icon). */
const RULE_ICONS: Record<BriefWeekLine['icon'], LucideIcon> = {
  rain: CloudRain,
  heat: ThermometerSun,
  cold: ThermometerSnowflake,
  wind: Wind,
  calendar: CalendarDays,
};

/**
 * The icon a week line is drawn with.
 *
 * @param line - The line.
 */
export function weekLineIcon(line: Pick<BriefWeekLine, 'water' | 'danger' | 'icon'>): LucideIcon {
  if (line.water) return WateringCan;
  if (line.danger) return AlertTriangle;
  return RULE_ICONS[line.icon] ?? CloudRain;
}

/**
 * The ink a week line's title is drawn in: its tone's, or the page's own.
 *
 * @param line - The line.
 */
export function weekLineInk(line: Pick<BriefWeekLine, 'tone'>): string {
  return line.tone ? TOKEN_TEXT[line.tone] : 'text-foreground';
}

/**
 * Whether the garden's filter is this line: the same plants, named by this
 * line (the ?plants= and ?for= filter the bar, the sheet and Today's Coming
 * up links share).
 *
 * @param line - The line.
 * @param settings - The garden's plantIds and plantsFor settings.
 */
export function weekLineIsFilter(line: Pick<BriefWeekLine, 'plantIds' | 'title'>, settings: { plantIds: string; plantsFor: string }): boolean {
  return line.plantIds.length > 0 && settings.plantIds === line.plantIds.join(',') && settings.plantsFor === line.title;
}

/**
 * The settings that narrow the garden to a line's plants, named by it.
 *
 * @param line - The line.
 */
export function weekLineFilter(line: Pick<BriefWeekLine, 'plantIds' | 'title'>): { plantIds: string; plantsFor: string; status: 'active' } {
  return { plantIds: line.plantIds.join(','), plantsFor: line.title, status: 'active' };
}

/** The settings that clear a line's filter. */
export const NO_LINE_FILTER = { plantIds: '', plantsFor: '' } as const;
